import { LoadingDots, PreIcon } from '@advanza/ui'
import RegionalPricingModal from 'components/subscriptions/RegionalPricingModal'
import ServicePriceContainer from 'containers/ServicePriceContainer'
import Option from 'interfaces/Options'
import Service from 'interfaces/Services'
import React, { useState } from 'react'

const PRICE_LABELS = {
    BASE_PRICE: 'Base price',
    WEBSITE_CLICK_PRICE: 'Website click price',
    PHONE_CLICK_PRICE: 'Phone click price',
    PRESENCE_SUBSCRIPTION_PRICE: 'Presence subscription price',
    NO_PRICE: 'no price',
} as const

interface Price {
    id: number
    amount: number
    amount_high?: number | null
    active: number
    option_id?: number
    is_website_click?: boolean
    is_phone_click?: boolean
    is_presence?: boolean
    end_date?: string | null
}

interface ServiceEntities {
    options: Record<number, Option>
    prices: Record<number, Price>
}

interface ExtendedService extends Service {
    prices?: number[]
    has_presence_subscription?: boolean
    display_real_phone_numbers?: boolean
}

interface ServicePricesActiveProps {
    service: ExtendedService | null
    serviceEntities: ServiceEntities
    isFetching: boolean
}

const ServicePricesActive: React.FC<ServicePricesActiveProps> = ({
    service,
    serviceEntities,
    isFetching,
}) => {
    const [isOpenRegionalPricing, setIsOpenRegionalPricing] = useState<boolean>(false)
    const { options, prices } = serviceEntities
    const priceIds = (service && service.prices) || []
    const currentPrices = priceIds.filter(
        (id: number) =>
            !prices[id].option_id &&
            prices[id].active === 1 &&
            !prices[id].is_website_click &&
            !prices[id].is_phone_click &&
            !prices[id].is_presence,
    )
    const currentPricesOptions = priceIds.filter(
        (id: number) =>
            prices[id].option_id &&
            prices[id].active === 1 &&
            !prices[id].is_website_click &&
            !prices[id].is_phone_click,
    )
    const currentPriceAll = currentPrices.length > 0 ? prices[currentPrices[0]] : null

    const websiteClickPriceId = priceIds.filter(
        (id: number) => prices[id].active && prices[id].is_website_click,
    )[0]
    const websiteClickPrice = websiteClickPriceId && prices[websiteClickPriceId]

    const phoneClickPriceId = priceIds.filter(
        (id: number) => prices[id].active && prices[id].is_phone_click,
    )[0]
    const phoneClickPrice = phoneClickPriceId && prices[phoneClickPriceId]

    const presencePriceId = priceIds.filter(
        (id: number) => prices[id].active && prices[id].is_presence,
    )[0]
    const presencePrice = presencePriceId && prices[presencePriceId]

    // Don't show table if no service is selected
    if (!service) {
        return (
            <div className="w-full">
                <div className="flex items-center gap-2 mb-5">
                    <PreIcon top icon="local_offer">
                        <h5 className="text-xl font-semibold text-bodyDark m-0">Active Prices</h5>
                    </PreIcon>
                </div>
                <div className="text-center py-12 px-6 bg-bg rounded-lg border border-dashed border-grey-500">
                    <div className="font-medium text-bodyDark mb-2">No service selected</div>
                    <div className="text-grey-800 text-md m-0">
                        Select a service from the dropdown above to view active prices
                    </div>
                </div>
            </div>
        )
    }

    const tableRows = [
        {
            id: 'base-price',
            cols: [
                <span key="base-name" className="font-medium text-grey-800 text-md">
                    {PRICE_LABELS.BASE_PRICE}
                </span>,
                (() => {
                    if (isFetching) {
                        return <LoadingDots />
                    }
                    if (currentPriceAll) {
                        return (
                            <span className="font-semibold text-bodyDark text-md">
                                €{currentPriceAll.amount.toFixed(2)}
                                {currentPriceAll.amount_high !== null &&
                                    currentPriceAll.amount_high !== undefined && (
                                        <> / {currentPriceAll.amount_high.toFixed(2)}</>
                                    )}
                            </span>
                        )
                    }
                    return (
                        <span className="font-semibold text-bodyDark text-md">
                            {PRICE_LABELS.NO_PRICE}
                        </span>
                    )
                })(),
                <span className="text-grey-800 text-md font-normal" key="base-date">
                    {currentPriceAll?.end_date || '∞'}
                </span>,
            ],
        },
        ...currentPricesOptions.map((id: number) => {
            const price = prices[id]
            if (!price) {
                return {
                    id: `price-option-${id}`,
                    cols: [
                        <span key="option-name" className="font-medium text-grey-800 text-md">
                            {PRICE_LABELS.NO_PRICE}
                        </span>,
                        <span key="option-price" className="font-semibold text-bodyDark text-md">
                            {PRICE_LABELS.NO_PRICE}
                        </span>,
                        <span key="option-date" className="text-grey-800 text-md font-normal">
                            {'∞'}
                        </span>,
                    ],
                    style: {},
                }
            }
            const optionId = price.option_id
            const option = optionId == null ? undefined : options[optionId]
            return {
                id: `price-option-${id}`,
                cols: [
                    <span key="option-name" className="font-medium text-grey-800 text-md">
                        {option?.value || PRICE_LABELS.NO_PRICE}
                    </span>,
                    <span key="option-price" className="font-semibold text-bodyDark text-md">
                        €{price.amount.toFixed(2)}
                        {price.amount_high !== null && price.amount_high !== undefined && (
                            <> / {price.amount_high.toFixed(2)}</>
                        )}
                    </span>,
                    <span key="option-date" className="text-grey-800 text-md font-normal">
                        {price.end_date || '∞'}
                    </span>,
                ],
                style:
                    (price.end_date &&
                        new Date() > new Date(price.end_date) && {
                            textDecoration: 'line-through',
                            opacity: 0.6,
                        }) ||
                    {},
            }
        }),
        {
            id: 'website-click',
            cols: [
                <span key="website-name" className="font-medium text-grey-800 text-md">
                    {PRICE_LABELS.WEBSITE_CLICK_PRICE}
                </span>,
                (() => {
                    if (isFetching) {
                        return <LoadingDots />
                    }
                    if (websiteClickPrice) {
                        return (
                            <span className="font-semibold text-bodyDark text-md">
                                €{websiteClickPrice.amount.toFixed(2)}
                            </span>
                        )
                    }
                    return (
                        <span className="font-semibold text-bodyDark text-md">
                            {PRICE_LABELS.NO_PRICE}
                        </span>
                    )
                })(),
                <span className="text-grey-800 text-md font-normal" key="website-date">
                    {(websiteClickPrice && websiteClickPrice.end_date) || '∞'}
                </span>,
            ],
        },
        {
            id: 'phone-click',
            cols: [
                <span key="phone-name" className="font-medium text-grey-800 text-md">
                    {PRICE_LABELS.PHONE_CLICK_PRICE}
                </span>,
                (() => {
                    if (isFetching) {
                        return <LoadingDots />
                    }
                    if (service?.display_real_phone_numbers) {
                        return <span className="font-semibold text-success text-md">Free</span>
                    }
                    if (phoneClickPrice) {
                        return (
                            <span className="font-semibold text-bodyDark text-md">
                                €{phoneClickPrice.amount.toFixed(2)}
                            </span>
                        )
                    }
                    return (
                        <span className="font-semibold text-bodyDark text-md">
                            {PRICE_LABELS.NO_PRICE}
                        </span>
                    )
                })(),
                <span className="text-grey-800 text-md font-normal" key="phone-date">
                    {(phoneClickPrice && phoneClickPrice.end_date) || '∞'}
                </span>,
            ],
        },
        {
            id: 'presence-subscription',
            cols: [
                <span key="presence-name" className="font-medium text-grey-800 text-md">
                    {PRICE_LABELS.PRESENCE_SUBSCRIPTION_PRICE}
                </span>,
                (() => {
                    if (isFetching) {
                        return <LoadingDots />
                    }
                    if (presencePrice) {
                        return (
                            <span className="font-semibold text-bodyDark text-md">
                                €{presencePrice.amount.toFixed(2)}
                            </span>
                        )
                    }
                    return (
                        <span className="font-semibold text-bodyDark text-md">
                            {PRICE_LABELS.NO_PRICE}
                        </span>
                    )
                })(),
                <span className="text-grey-800 text-md font-normal" key="presence-date">
                    {(presencePrice && presencePrice.end_date) || '∞'}
                </span>,
            ],
        },
    ]

    return (
        <div className="w-full">
            <div className="flex items-center gap-2 mb-5">
                <PreIcon top icon="local_offer">
                    <h5 className="text-xl font-semibold text-bodyDark m-0">Active Prices</h5>
                </PreIcon>
            </div>
            <div className="bg-white border border-grey-500 rounded-xl overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="bg-bg border-b-2 border-grey-500">
                            <th className="py-3 px-6 text-sm font-semibold text-grey-800 uppercase tracking-[0.5px] text-left leading-tight">
                                Price Type
                            </th>
                            <th className="py-3 px-6 text-sm font-semibold text-grey-800 uppercase tracking-[0.5px] text-left leading-tight">
                                Amount
                            </th>
                            <th className="py-3 px-6 text-sm font-semibold text-grey-800 uppercase tracking-[0.5px] text-left leading-tight">
                                End Date
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableRows.map((row) => (
                            <tr
                                key={row.id}
                                className="border-b border-grey-200 hover:bg-bg transition-colors"
                                style={'style' in row ? row.style : undefined}>
                                {row.cols.map((col, index) => (
                                    <td
                                        key={index}
                                        className="py-3 px-6 text-md text-bodyDark align-middle leading-tight">
                                        {col}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="mt-5 pt-5 border-t border-grey-500">
                <button
                    type="button"
                    onClick={() => setIsOpenRegionalPricing(true)}
                    className="text-primary-600 no-underline text-md font-medium inline-flex items-center gap-1.5 transition-colors duration-200 hover:text-primary-700 hover:underline cursor-pointer focus:outline-2 focus:outline-primary-600 focus:outline-offset-2 focus:rounded bg-transparent border-none p-0">
                    <PreIcon icon="map">Price regions</PreIcon>
                </button>
            </div>
            <RegionalPricingModal
                service={service}
                isOpen={isOpenRegionalPricing}
                setIsOpen={setIsOpenRegionalPricing}
            />
        </div>
    )
}

export default ServicePriceContainer(ServicePricesActive)
